import { useMemo } from 'react'
import type { JSX } from 'react'
import { NavLink } from 'react-router-dom'

import { clsx } from 'clsx'

import { Button, Flex } from '@buffer-mono/popcorn'

import { FeedItemIcon } from '~publish/pages/Create/Feed/components/FeedIcon/FeedIcon'

import { useOverflowItems } from '../../hooks'
import type { ChipItem, CollectionData, FeedData } from '../../types'
import { CollectionChip } from '../CollectionChip'
import { CollectionNameEditor } from '../CollectionNameEditor'
import { MoreFeedsDropdown } from '../MoreFeedsDropdown'

import styles from './FeedsChipsList.module.css'

// Gap value in pixels - matches CSS var(--space-sm) which is typically 12px
const GAP_SIZE = 12

interface FeedsChipsListProps {
  feeds: FeedData[]
  collections: CollectionData[]
  isAddingCollection: boolean
  onCreateCollection: (name: string) => Promise<void>
  onCancelCollection: () => void
}

export function FeedsChipsList({
  feeds,
  collections,
  isAddingCollection,
  onCreateCollection,
  onCancelCollection,
}: FeedsChipsListProps): JSX.Element {
  // Combine feeds and collections into a single array for overflow calculation
  const allChipItems = useMemo<ChipItem[]>(() => {
    const feedItems: ChipItem[] = feeds.map((feed) => ({
      type: 'feed' as const,
      data: feed,
    }))
    const collectionItems: ChipItem[] = collections.map((collection) => ({
      type: 'collection' as const,
      data: collection,
    }))
    return [...feedItems, ...collectionItems]
  }, [feeds, collections])

  // Use overflow hook to calculate visible and overflow items
  const {
    visibleItems,
    overflowItems,
    containerRef,
    itemRefs,
    allFeedsRef,
    collectionEditorRef,
    moreButtonRef,
  } = useOverflowItems<ChipItem>({
    items: allChipItems,
    gap: GAP_SIZE,
    dependencies: [isAddingCollection], // Recalculate when collection editor appears/disappears
  })

  const hasOverflow = overflowItems.length > 0

  return (
    <div ref={containerRef} className={styles.container}>
      {/* All Feeds button - positioned on the left */}
      <div ref={allFeedsRef} className={styles.allFeedsSection}>
        <Button
          as={NavLink}
          variant="secondary"
          to="/create/feeds"
          size="small"
          className={styles.button}
          end
        >
          All Feeds
        </Button>
      </div>

      {/* Chips container - hugs the chips that fit, clips the rest */}
      <div className={styles.chipsContainer}>
        <Flex gap="sm" className={styles.chipsList}>
          {/* Render all items for measurement - hidden items are absolutely positioned off-screen */}
          {allChipItems.map((item: ChipItem, index: number) => {
            const isVisible = index < visibleItems.length

            return (
              <div
                key={item.data.id}
                ref={itemRefs[index]}
                className={clsx(
                  styles.chipWrapper,
                  !isVisible && styles.chipWrapperHidden,
                )}
                aria-hidden={!isVisible}
              >
                {item.type === 'feed' ? (
                  <Button
                    as={NavLink}
                    variant="secondary"
                    to={`/create/feeds/${item.data.id}`}
                    size="small"
                    className={styles.button}
                  >
                    <FeedItemIcon
                      feedName={item.data.name}
                      url={item.data.url}
                      size="xsmall"
                    />
                    {item.data.name}
                  </Button>
                ) : (
                  <CollectionChip collection={item.data} />
                )}
              </div>
            )
          })}

          {/* Inline collection name editor */}
          {isAddingCollection && (
            <div ref={collectionEditorRef}>
              <CollectionNameEditor
                onSubmit={(name): void => {
                  void onCreateCollection(name)
                }}
                onCancel={onCancelCollection}
              />
            </div>
          )}
        </Flex>
      </div>

      {/* Sits outside the clipping container on purpose: it is the only route
          to the overflowed feeds, so it must never be clipped away. Stays
          rendered while hidden so the hook can measure the width to reserve. */}
      <div
        ref={moreButtonRef}
        className={clsx(
          styles.moreFeedsSection,
          !hasOverflow && styles.moreFeedsSectionHidden,
        )}
        aria-hidden={!hasOverflow}
      >
        <MoreFeedsDropdown
          overflowItems={overflowItems}
          buttonClassName={styles.button}
        />
      </div>
    </div>
  )
}
